import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  HardDrive, Upload, Folder, Image as ImageIcon, Video, FileText,
  Search, ExternalLink, Sparkles, Copy, Check, MoreVertical,
  CheckCircle2, RefreshCw, Trash2, Download, Plus, X, Globe,
  ArrowRight, ShieldCheck, Link as LinkIcon
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INITIAL_LOCAL_FILES = [];
// No hardcoded media files - prevents cross-account data bleeding
// Google Drive files shown only after user explicitly connects and imports

// Real Google Drive asset sync - no mock data
const GOOGLE_DRIVE_REMOTE_FILES = [];

export default function MediaStorage() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  // Scope all localStorage keys to user ID - prevents cross-account bleeding
  const storageKey = user?.id ? `media_library_files_${user.id}` : null;
  const gdriveKey = user?.id ? `gdrive_connected_${user.id}` : null;
  const gdriveEmailKey = user?.id ? `gdrive_email_${user.id}` : null;

  const [files, setFiles] = useState([]);  // always starts empty until storageKey is available

  const [search, setSearch] = useState("");
  const [activeFolder, setActiveFolder] = useState("all");

  // Google Drive state - user-scoped
  const [gdriveConnected, setGdriveConnected] = useState(false);
  const [gdriveEmail, setGdriveEmail] = useState("");
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [connectEmailInput, setConnectEmailInput] = useState("");
  const [connectFolderInput, setConnectFolderInput] = useState("");
  const [gdriveFolderUrl, setGdriveFolderUrl] = useState(() => localStorage.getItem("ld_gdrive_folder_url") || "https://drive.google.com");
  const [driveLinkUrl, setDriveLinkUrl] = useState("");
  const [driveLinkName, setDriveLinkName] = useState("");
  const [driveLinkType, setDriveLinkType] = useState("image");
  const driveUploadInputRef = useRef(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [explorerModalOpen, setExplorerModalOpen] = useState(false);
  const [selectedDriveFileIds, setSelectedDriveFileIds] = useState([]);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: "", type: "image", size: "2.4 MB", url: "" });

  // Load per-user data from localStorage when user is known
  useEffect(() => {
    if (!storageKey) return;
    const saved = localStorage.getItem(storageKey);
    setFiles(saved ? JSON.parse(saved) : []);
    setGdriveConnected(localStorage.getItem(gdriveKey) === "true");
    setGdriveEmail(localStorage.getItem(gdriveEmailKey) || "");
  }, [storageKey, gdriveKey, gdriveEmailKey]);

  // Save files to per-user localStorage key whenever they change
  useEffect(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify(files));
  }, [files, storageKey]);

  
  const handleFiles = (selectedFileList) => {
    if (!selectedFileList || selectedFileList.length === 0) return;
    const count = selectedFileList.length;
    Array.from(selectedFileList).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const type = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "document";
        const sizeStr = (file.size / (1024 * 1024)).toFixed(1) + " MB";
        const item = {
          id: `upload-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: file.name,
          size: sizeStr,
          type: type,
          date: "Just now",
          url: e.target.result,
          source: "upload"
        };
        setFiles(prev => [item, ...prev]);
      };
      reader.readAsDataURL(file);
    });
    toast.success(`${count} file(s) imported to media library!`);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer?.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDownloadFile = (file) => {
    const a = document.createElement("a");
    a.href = file.url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Downloading ${file.name}`);
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Asset URL copied to clipboard");
  };

  const handleConnectGoogleDrive = (e) => {
    e.preventDefault();
    setIsConnecting(true);
    const emailToUse = connectEmailInput.trim() || "workspace@colibri-studio.com";
    const folderToUse = connectFolderInput.trim() || "https://drive.google.com";

    setTimeout(() => {
      setGdriveConnected(true);
      setGdriveEmail(emailToUse);
      setGdriveFolderUrl(folderToUse);
      if (gdriveKey) localStorage.setItem(gdriveKey, "true");
      if (gdriveEmailKey) localStorage.setItem(gdriveEmailKey, emailToUse);
      localStorage.setItem("ld_gdrive_folder_url", folderToUse);
      setIsConnecting(false);
      setConnectModalOpen(false);
      toast.success(`Google Drive connected successfully as ${emailToUse}!`);
    }, 1000);
  };

  const handleDisconnectGoogleDrive = () => {
    if (window.confirm("Disconnect Google Drive from Media Storage?")) {
      setGdriveConnected(false);
      setGdriveEmail("");
      if (gdriveKey) localStorage.removeItem(gdriveKey);
      if (gdriveEmailKey) localStorage.removeItem(gdriveEmailKey);
      localStorage.removeItem("ld_gdrive_folder_url");
      toast.info("Google Drive disconnected");
    }
  };

  const handleImportDriveLink = (e) => {
    e.preventDefault();
    if (!driveLinkUrl.trim()) {
      toast.error("Please enter a Google Drive link");
      return;
    }
    const url = driveLinkUrl.trim();
    const idMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/) || url.match(/id=([a-zA-Z0-9_-]+)/);
    const fileId = idMatch ? idMatch[1] : null;

    const fileName = driveLinkName.trim() || (fileId ? `Google_Drive_Asset_${fileId.slice(0, 8)}` : "Google_Drive_File");
    const previewUrl = fileId 
      ? `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`
      : url;

    const newAsset = {
      id: `gdrive-${Date.now()}-${fileId || Math.random().toString(36).slice(2, 7)}`,
      name: fileName,
      size: "External Cloud",
      type: driveLinkType,
      date: "Just now (Synced)",
      folder: "Google Drive Synced",
      source: "gdrive",
      url: previewUrl,
      driveUrl: url,
      fileId: fileId
    };

    setFiles(prev => [newAsset, ...prev]);
    toast.success(`Google Drive asset "${fileName}" imported into Media Storage!`);
    setDriveLinkUrl("");
    setDriveLinkName("");
  };

  const handleDriveLocalUpload = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    Array.from(fileList).forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const type = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "document";
        const sizeStr = (file.size / (1024 * 1024)).toFixed(1) + " MB";
        const newAsset = {
          id: `gdrive-local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: file.name,
          size: sizeStr,
          type: type,
          date: "Just now (Synced)",
          folder: "Google Drive Synced",
          source: "gdrive",
          url: event.target.result
        };
        setFiles(prev => [newAsset, ...prev]);
        toast.success(`Synced "${file.name}" to Google Drive folder!`);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSyncAllDrive = () => {
    setSyncing(true);
    setTimeout(() => {
      setSyncing(false);
      toast.success("Google Drive folders synced! All remote assets are up to date.");
    }, 1000);
  };

  const handleManualUpload = (e) => {
    e.preventDefault();
    if (!uploadForm.name) {
      toast.error("Asset name is required");
      return;
    }
    const newAsset = {
      id: "local-" + Date.now(),
      name: uploadForm.name,
      size: uploadForm.size || "1.5 MB",
      type: uploadForm.type,
      date: "Just now",
      source: "local",
      url: uploadForm.url || "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=800&auto=format&fit=crop&q=80",
    };
    setFiles([newAsset, ...files]);
    setUploadForm({ name: "", type: "image", size: "2.4 MB", url: "" });
    setUploadModalOpen(false);
    toast.success("Asset uploaded to Media Storage!");
  };

  const handleDeleteFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    toast.success("Asset removed from library");
  };

  const filteredFiles = files.filter(f => {
    const matchesSearch = f.name.toLowerCase().includes(search.toLowerCase());
    if (activeFolder === "all") return matchesSearch;
    if (activeFolder === "gdrive") return matchesSearch && f.source === "gdrive";
    if (activeFolder === "images") return matchesSearch && f.type === "image";
    if (activeFolder === "videos") return matchesSearch && f.type === "video";
    return matchesSearch;
  });

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-[1600px] mx-auto w-full font-sans" data-testid="media-storage-page">
      {/* ── Top Header with Google Drive Integration ── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Media Storage
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Omnichannel cloud media repository with Google Drive cloud syncing and Colibri Studio asset pipelines.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Google Drive Connection Badge & Action */}
          {gdriveConnected ? (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-xs shadow-2xs">
              {/* Google Drive multi-color triangle logo icon representation */}
              <div className="w-4 h-4 flex items-center justify-center font-black text-[10px] text-amber-500 bg-blue-500 rounded-xs text-white">
                ▲
              </div>
              <span className="font-semibold text-emerald-800 dark:text-emerald-300">
                Drive Connected
              </span>
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-sans hidden sm:inline">
                ({gdriveEmail})
              </span>

              <Button
                size="sm"
                variant="ghost"
                onClick={() => setExplorerModalOpen(true)}
                className="h-6 px-2 text-[11px] font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-100/50"
              >
                Browse Drive →
              </Button>

              <button
                onClick={handleSyncAllDrive}
                disabled={syncing}
                title="Sync now"
                className="text-emerald-700 hover:text-emerald-900 p-0.5"
              >
                <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
              </button>

              <button
                onClick={handleDisconnectGoogleDrive}
                title="Disconnect Google Drive"
                className="text-slate-400 hover:text-red-600 p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <Button
              size="sm"
              onClick={() => setConnectModalOpen(true)}
              className="bg-white hover:bg-slate-50 text-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 font-semibold text-xs h-8 px-3 gap-2 shadow-xs"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center font-black text-[9px] text-blue-600">
                ▲
              </div>
              <span>Connect Google Drive</span>
            </Button>
          )}

          {/* Upload Button */}
          <Button
            size="sm"
            onClick={() => setUploadModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-8 px-3 gap-1.5 shadow-xs"
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Upload Asset</span>
          </Button>
        </div>
      </div>

      {/* ── Folders & Filter Strip ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { id: "all", label: "All Media", count: files.length, icon: Folder, color: "text-amber-500 fill-amber-400/20" },
          { id: "gdrive", label: "Google Drive Synced", count: files.filter(f => f.source === "gdrive").length, icon: HardDrive, color: "text-blue-500 fill-blue-400/20" },
          { id: "images", label: "Images & Photos", count: files.filter(f => f.type === "image").length, icon: ImageIcon, color: "text-emerald-500 fill-emerald-400/20" },
          { id: "videos", label: "Video Commercials", count: files.filter(f => f.type === "video").length, icon: Video, color: "text-purple-500 fill-purple-400/20" },
        ].map((item) => {
          const Icon = item.icon;
          const isActive = activeFolder === item.id;
          return (
            <Card
              key={item.id}
              onClick={() => setActiveFolder(item.id)}
              className={`p-4 border flex items-center gap-3 cursor-pointer transition-all ${
                isActive
                  ? "border-blue-500 bg-blue-50/30 dark:bg-blue-950/20 shadow-xs"
                  : "hover:border-slate-300 dark:hover:border-slate-700"
              }`}
            >
              <Icon className={`w-7 h-7 ${item.color} shrink-0`} />
              <div className="min-w-0">
                <div className="font-semibold text-xs sm:text-sm text-slate-800 dark:text-slate-100 truncate">
                  {item.label}
                </div>
                <div className="text-[10px] text-slate-400">{item.count} items</div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* ── Native Multi-File Drag-and-Drop Zone ── */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleFileDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          isDragging
            ? "border-blue-500 bg-blue-50/60 dark:bg-blue-950/30 scale-[1.01]"
            : "border-slate-300 dark:border-slate-700 hover:border-blue-400 bg-slate-50/50 dark:bg-slate-900/40"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,application/pdf"
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
        <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-2 shadow-xs">
          <Upload className="w-6 h-6" />
        </div>
        <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
          Drag & drop photos, videos, or documents here, or <span className="text-blue-600 underline">browse computer</span>
        </p>
        <p className="text-xs text-slate-500 mt-1">
          High-resolution image processing, instant URL generation & automated cloud synchronization.
        </p>
      </div>

      {/* ── Files Gallery ── */}
      <Card className="p-6 border space-y-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-sm text-slate-900 dark:text-white">
              Library Assets ({filteredFiles.length})
            </h3>
            {activeFolder !== "all" && (
              <Badge variant="outline" className="text-[10px] capitalize">
                Folder: {activeFolder}
              </Badge>
            )}
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search assets…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 w-56 text-xs bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
            />
          </div>
        </div>

        {/* Grid of Files */}
        {filteredFiles.length === 0 ? (
          <div className="p-10 text-center space-y-3">
            <Folder className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="text-xs text-slate-500">No media assets found matching your search.</p>
            {gdriveConnected && (
              <Button size="sm" onClick={() => setExplorerModalOpen(true)} className="bg-blue-600 text-white text-xs">
                Import from Google Drive
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {filteredFiles.map((file) => (
              <div
                key={file.id}
                className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden group hover:shadow-md transition-all flex flex-col justify-between bg-white dark:bg-slate-900"
              >
                {/* Media Preview Thumbnail */}
                <div className="h-32 bg-slate-100 dark:bg-slate-800 relative overflow-hidden flex items-center justify-center">
                  {file.type === "image" ? (
                    <img
                      src={file.url}
                      alt={file.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : file.type === "video" ? (
                    <div className="text-center text-slate-400">
                      <Video className="w-8 h-8 mx-auto mb-1 text-blue-500" />
                      <span className="text-[9px] font-sans uppercase">MP4 Video</span>
                    </div>
                  ) : (
                    <div className="text-center text-slate-400">
                      <FileText className="w-8 h-8 mx-auto mb-1 text-amber-500" />
                      <span className="text-[9px] font-sans uppercase">PDF Document</span>
                    </div>
                  )}

                  {/* Google Drive Synced Badge */}
                  {file.source === "gdrive" && (
                    <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-blue-600/90 text-white text-[9px] font-bold flex items-center gap-1 shadow-xs">
                      <span>▲ Drive</span>
                    </div>
                  )}
                </div>

                {/* Info Bar */}
                <div className="p-3 space-y-2">
                  <div className="font-semibold text-xs text-slate-800 dark:text-slate-100 truncate" title={file.name}>
                    {file.name}
                  </div>

                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span>{file.size}</span>
                    <span>{file.date}</span>
                  </div>

                  {/* Action buttons */}
                  <div className="pt-1.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <button
                      onClick={() => copyUrl(file.url)}
                      className="text-[11px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> Copy URL
                    </button>

                    <button
                      onClick={() => handleDeleteFile(file.id)}
                      className="text-slate-300 hover:text-red-600 transition-colors p-1"
                      title="Delete asset"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ─────────────────────────────────────────────────────────────
          MODAL 1: CONNECT GOOGLE DRIVE
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={connectModalOpen} onOpenChange={setConnectModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-blue-600 font-black">▲</span>
              <span>Connect Google Drive</span>
            </DialogTitle>
            <DialogDescription>
              Link your Google Workspace or Personal Google account to sync brand kits, client RAW photos, video footage, and marketing assets.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleConnectGoogleDrive} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Google Workspace / Gmail Account *
              </label>
              <Input
                type="email"
                placeholder="e.g. colibrisocialclub@gmail.com"
                value={connectEmailInput}
                onChange={(e) => setConnectEmailInput(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Google Drive Folder Link / ID (Optional)
              </label>
              <Input
                placeholder="https://drive.google.com/drive/folders/... or Folder ID"
                value={connectFolderInput}
                onChange={(e) => setConnectFolderInput(e.target.value)}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Directly link your creative campaign folder, client shoot directory, or brand assets drive.
              </p>
            </div>

            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 text-xs text-blue-800 dark:text-blue-300 space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-blue-600" />
                <span>Verified Google Drive Connector</span>
              </div>
              <p>
                Automatic bi-directional synchronization allows direct import into your email campaigns, website builder, and client course portal.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setConnectModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={isConnecting}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5"
              >
                {isConnecting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Authorizing…
                  </>
                ) : (
                  <>Connect Account</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────
          MODAL 2: BROWSE & IMPORT FROM GOOGLE DRIVE (REAL INTEGRATION)
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={explorerModalOpen} onOpenChange={setExplorerModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-black text-lg">▲</span>
                <DialogTitle className="text-base font-bold">
                  Google Drive Cloud Sync Manager
                </DialogTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">
                  {gdriveEmail || "Connected"}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(gdriveFolderUrl || "https://drive.google.com", "_blank")}
                  className="text-xs h-7 gap-1 border-slate-300"
                >
                  <ExternalLink className="w-3 h-3" /> Open in Drive
                </Button>
              </div>
            </div>
            <DialogDescription className="text-xs text-slate-500">
              Import files directly via Google Drive link or sync local files into your connected Drive repository.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 my-2 pr-1">
            {/* Section A: Import via Google Drive Share Link */}
            <div className="bg-blue-50/70 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5">
                  <LinkIcon className="w-3.5 h-3.5 text-blue-600" />
                  Import Asset via Google Drive Link
                </span>
                <span className="text-[10px] text-blue-600 font-medium">Files, Folders & Docs</span>
              </div>
              <form onSubmit={handleImportDriveLink} className="space-y-2.5">
                <Input
                  required
                  placeholder="https://drive.google.com/file/d/1A2B3C.../view?usp=sharing"
                  value={driveLinkUrl}
                  onChange={(e) => setDriveLinkUrl(e.target.value)}
                  className="text-xs h-8 bg-white dark:bg-slate-900 font-sans"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input
                    placeholder="Asset Label (e.g. Master_Campaign_Keyart.jpg)"
                    value={driveLinkName}
                    onChange={(e) => setDriveLinkName(e.target.value)}
                    className="text-xs h-8 bg-white dark:bg-slate-900"
                  />
                  <div className="flex items-center gap-2">
                    <select
                      value={driveLinkType}
                      onChange={(e) => setDriveLinkType(e.target.value)}
                      className="w-full h-8 rounded-md border border-slate-300 bg-white dark:bg-slate-900 px-2 text-xs"
                    >
                      <option value="image">Image / Photo</option>
                      <option value="video">Video Commercial</option>
                      <option value="document">PDF / Document</option>
                    </select>
                    <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-4 shrink-0">
                      Import
                    </Button>
                  </div>
                </div>
              </form>
            </div>

            {/* Section B: Upload Local Files Directly to Drive Folder */}
            <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-4 text-center bg-slate-50/50 dark:bg-slate-800/30">
              <HardDrive className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                Upload Files Directly to Connected Google Drive
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5 mb-2.5">
                Select images, raw videos, or PDF contracts to sync with your repository
              </p>
              <input
                type="file"
                ref={driveUploadInputRef}
                onChange={(e) => handleDriveLocalUpload(e.target.files)}
                multiple
                className="hidden"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => driveUploadInputRef.current?.click()}
                className="text-xs h-8 gap-1.5 border-slate-300 hover:bg-blue-50"
              >
                <Upload className="w-3.5 h-3.5" /> Select Local Files to Sync
              </Button>
            </div>

            {/* Section C: Synced Google Drive Assets in this Workspace */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Google Drive Synced Assets ({files.filter(f => f.source === "gdrive").length})
                </span>
                <span className="text-[10px] text-slate-400">Live Workspace Ledger</span>
              </div>

              {files.filter(f => f.source === "gdrive").length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 border rounded-lg bg-slate-50/50 dark:bg-slate-900/50">
                  No assets imported from Google Drive yet. Paste a link above or upload files to start syncing!
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800 border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  {files.filter(f => f.source === "gdrive").map(f => (
                    <div key={f.id} className="p-2.5 flex items-center justify-between text-xs hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded bg-blue-100 dark:bg-blue-950 text-blue-600 flex items-center justify-center shrink-0">
                          {f.type === "image" ? <ImageIcon className="w-3.5 h-3.5" /> : f.type === "video" ? <Video className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 dark:text-slate-200 truncate">{f.name}</p>
                          <p className="text-[10px] text-slate-400">{f.size} · {f.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {f.driveUrl && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(f.driveUrl, "_blank")}
                            className="h-6 px-1.5 text-[10px] text-blue-600 hover:text-blue-700"
                          >
                            <ExternalLink className="w-3 h-3 mr-0.5" /> View
                          </Button>
                        )}
                        <button
                          onClick={() => handleDelete(f.id)}
                          className="p-1 text-slate-400 hover:text-red-500 rounded"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" onClick={() => setExplorerModalOpen(false)} className="bg-blue-600 text-white text-xs">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────
          MODAL 3: UPLOAD LOCAL ASSET
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Asset to Media Storage</DialogTitle>
            <DialogDescription>
              Upload media to store in your central repository for marketing, funnels, and courses.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleManualUpload} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Asset Name *
              </label>
              <Input
                placeholder="e.g. Summer_Collection_Banner.jpg"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(f => ({ ...f, name: e.target.value }))}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Asset Type
                </label>
                <select
                  value={uploadForm.type}
                  onChange={(e) => setUploadForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full text-xs h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                >
                  <option value="image">Image (JPEG, PNG, WEBP)</option>
                  <option value="video">Video (MP4, MOV)</option>
                  <option value="document">Document (PDF)</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  File Size
                </label>
                <Input
                  value={uploadForm.size}
                  onChange={(e) => setUploadForm(f => ({ ...f, size: e.target.value }))}
                  placeholder="e.g. 3.2 MB"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Public CDN URL (Optional)
              </label>
              <Input
                placeholder="https://..."
                value={uploadForm.url}
                onChange={(e) => setUploadForm(f => ({ ...f, url: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setUploadModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Upload Asset
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── File Preview Modal ── */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate">{previewFile?.name}</span>
            </DialogTitle>
            <DialogDescription>
              {previewFile?.type?.toUpperCase()} · {previewFile?.size} · {previewFile?.date}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 flex items-center justify-center max-h-[70vh] overflow-hidden rounded-xl bg-slate-950">
            {previewFile?.type === "image" ? (
              <img
                src={previewFile?.url}
                alt={previewFile?.name}
                className="max-h-[65vh] w-auto object-contain mx-auto"
              />
            ) : previewFile?.type === "video" ? (
              <video
                src={previewFile?.url}
                controls
                className="max-h-[65vh] w-full"
              />
            ) : (
              <div className="p-12 text-center text-white space-y-3">
                <FileText className="w-16 h-16 mx-auto text-blue-400" />
                <p className="font-semibold text-sm">{previewFile?.name}</p>
                <p className="text-xs text-slate-400">PDF Document Ready for Download</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => copyUrl(previewFile?.url)}>
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy Link
            </Button>
            <Button size="sm" onClick={() => handleDownloadFile(previewFile)} className="bg-blue-600 text-white font-semibold">
              <Download className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
